import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const ProductionCost = () => {
  // State variables for Production Cost calculation
  const [directMaterials, setDirectMaterials] = useState(0); // Direct materials cost
  const [directLabor, setDirectLabor] = useState(0); // Direct labor cost
  const [manufacturingOverhead, setManufacturingOverhead] = useState(0); // Manufacturing overhead
  const [unitsProduced, setUnitsProduced] = useState(0); // Units produced
  const [productionCost, setProductionCost] = useState(0); // Calculated production cost

  // Event handlers to update state values
  const handleDirectMaterialsChange = (e) =>
    setDirectMaterials(parseFloat(e.target.value) || 0);
  const handleDirectLaborChange = (e) =>
    setDirectLabor(parseFloat(e.target.value) || 0);
  const handleManufacturingOverheadChange = (e) =>
    setManufacturingOverhead(parseFloat(e.target.value) || 0);
  const handleUnitsProducedChange = (e) =>
    setUnitsProduced(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Production Cost
  useEffect(() => {
    if (unitsProduced > 0) {
      const totalCost =
        (directMaterials + directLabor + manufacturingOverhead) / unitsProduced;
      setProductionCost(totalCost);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [directMaterials, directLabor, manufacturingOverhead, unitsProduced]);

  return (
    <Layout title="Production Cost Calculator" path="production-cost">
      <section className="section-padding" id="production-cost">
        <div className="container mt-5">
          <div className="row">
            {/* Production Cost Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Production Cost Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Direct Materials */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="directMaterials" className="form-label">
                        Direct Materials Cost
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Direct Materials:</strong> The cost of raw materials directly involved in the production of a product."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="directMaterials"
                        placeholder="Enter direct materials cost"
                        value={directMaterials || ""}
                        onChange={handleDirectMaterialsChange}
                      />
                    </div>

                    {/* Input for Direct Labor */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="directLabor" className="form-label">
                        Direct Labor Cost
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Direct Labor:</strong> The cost of wages paid to workers directly involved in manufacturing."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="directLabor"
                        placeholder="Enter direct labor cost"
                        value={directLabor || ""}
                        onChange={handleDirectLaborChange}
                      />
                    </div>

                    {/* Input for Manufacturing Overhead */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="manufacturingOverhead"
                        className="form-label"
                      >
                        Manufacturing Overhead
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Manufacturing Overhead:</strong> Indirect costs related to production, like utilities, maintenance, and equipment depreciation."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="manufacturingOverhead"
                        placeholder="Enter manufacturing overhead cost"
                        value={manufacturingOverhead || ""}
                        onChange={handleManufacturingOverheadChange}
                      />
                    </div>

                    {/* Input for Units Produced */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="unitsProduced" className="form-label">
                        Units Produced
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Units Produced:</strong> The total number of finished goods produced."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="unitsProduced"
                        placeholder="Enter number of units produced"
                        value={unitsProduced || ""}
                        onChange={handleUnitsProducedChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Production Cost per Unit:{" "}
                        <span className="fw-bold ms-3">
                          {productionCost === 0
                            ? "0"
                            : productionCost.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Production Cost Calculation
                </h3>

                {/* What is Production Cost? */}
                <h4 className="mt-3 mb-3">What is Production Cost?</h4>
                <p>
                  **Production Cost** refers to the total expense incurred to
                  manufacture a product, including direct materials, direct
                  labor, and manufacturing overhead, divided by the total number
                  of units produced.
                </p>

                {/* Why Use the Production Cost Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Production Cost Calculator?
                </h4>
                <p>
                  This calculator helps determine the cost per unit for a
                  production run, which is essential for setting competitive
                  pricing and managing expenses effectively.
                </p>

                {/* Benefits of Using the Production Cost Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Production Cost Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps with Pricing Strategies:</strong> Provides
                    accurate cost data for setting product prices.
                  </li>
                  <li>
                    <strong>Improves Cost Control:</strong> Identifies
                    cost-saving opportunities by analyzing production expenses.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Production Cost Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Manufacturing:</strong> Tracks costs and ensures
                    efficiency in production lines.
                  </li>
                  <li>
                    <strong>Assembly Lines:</strong> Calculates unit costs to
                    improve profitability.
                  </li>
                  <li>
                    <strong>Product Development:</strong> Assists in budgeting
                    and financial planning for new products.
                  </li>
                </ul>

                <h5 className="mt-3 mb-2">
                  How does production cost impact pricing?
                </h5>
                <p>
                  Production cost directly affects pricing decisions. Higher
                  production costs may require higher selling prices to maintain
                  profitability, while lower costs allow for competitive
                  pricing.
                </p>

                <h5 className="mt-3 mb-2">
                  Can this calculator be used for service-based businesses?
                </h5>
                <p>
                  While primarily designed for manufacturing, this calculator
                  can be adapted for service businesses by considering inputs
                  like labor and operational costs for service delivery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProductionCost;
